import type { Theme } from "@mui/material/styles";
import type { SystemStyleObject } from "@mui/system";

/** The fingertip floor for a control on a phone or a touch screen, in px. */
export const TOUCH_TARGET = 44;

/**
 * The media query under which a control must be at least {@link TOUCH_TARGET} on each side: a phone
 * width, or any coarse pointer — a touch laptop is wide but still pressed with a finger.
 *
 * Scoped this way so a mouse on a desktop keeps MUI's denser defaults. One copy, so every control
 * grows under the same condition. See "Layout by width" in
 * [PERMISSIONS_UI_DESIGN.md](../../../../docs/design/permissions/PERMISSIONS_UI_DESIGN.md).
 */
export function touchTargetQuery(theme: Theme): string {
  return `${theme.breakpoints.down("sm")}, (pointer: coarse)`;
}

/**
 * An `sx` value that holds a control to {@link TOUCH_TARGET} on each side under
 * {@link touchTargetQuery}, and changes nothing for a mouse on a wide screen.
 *
 * With `within`, the floor goes on the matching descendants rather than on the element itself —
 * for a component whose pressable parts are its children, such as the page buttons of a
 * `Pagination`. Combine with a control's own `sx` as an array: `sx={[touchTargetSx(), { … }]}`.
 */
export function touchTargetSx(within?: string): (theme: Theme) => SystemStyleObject<Theme> {
  const floor = { minWidth: TOUCH_TARGET, minHeight: TOUCH_TARGET };
  return (theme) => ({ [touchTargetQuery(theme)]: within ? { [within]: floor } : floor });
}
